import { Button } from '@ads/components-react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentParcelViewModel } from '@/shared/api/generated';
import { Alert, ProgressBar, Spinner } from '@/shared/ui/atoms';
import { ConfirmDialog } from '@/shared/ui/molecules';

import { useParcelBillet } from '../../../hooks/useParcelBillet';
import { resolveBilletIssueRoute } from '../../../utils/billetIssueRoute';
import { formatContractDate } from '../../../utils/formatContractDate';
import { EMPTY_VALUE, formatCurrency } from '../../../utils/formatCurrency';

/** Props de `ParcelBilletModal`. */
export interface ParcelBilletModalProps {
  /** Contrato da rota, para invalidar a grid quando o registro conclui. */
  readonly financialPersonId: number | null;
  /** Parcela alvo. `null` mantém o diálogo fechado. */
  readonly parcelId: number | null;
  /** A parcela — é dela que sai a decisão entre baixar e registrar. */
  readonly parcel?: IndividualPaymentParcelViewModel | undefined;
  /** Total de parcelas do contrato, para o rótulo `3/12`. */
  readonly numberParcels?: number | null | undefined;
  readonly onClose: () => void;
}

/**
 * Diálogo do "Gerar boleto" (regra 2 do grid legado).
 *
 * O legado escondia dois fluxos atrás de um botão só, e a diferença importa para
 * o operador: **baixar** um boleto que já existe é instantâneo; **registrar** no
 * PecegePay depende do provedor e leva segundos a minutos. Este diálogo diz qual
 * dos dois vai acontecer **antes** de confirmar, e no caminho do registro mostra
 * a espera em vez de parecer travado.
 *
 * Quem decide o caminho é `resolveBilletIssueRoute`, com os quatro campos que o
 * `generateBillet` do legado recebia. O acompanhamento para quando o provedor
 * carimba `registeredAt` ou `error` no boleto.
 *
 * Estourar o teto de leituras **não** cancela nada no provedor — a mensagem diz
 * isso, para o operador não registrar de novo achando que falhou.
 */
export function ParcelBilletModal({
  financialPersonId,
  parcelId,
  parcel,
  numberParcels,
  onClose,
}: ParcelBilletModalProps) {
  const { t } = useTranslation('individualPayment');
  const billet = useParcelBillet(financialPersonId, parcelId);

  // Fechado é desmontado, como nos outros diálogos da tela: descarta a fase e o
  // erro da tentativa anterior sem um efeito de limpeza para manter em sincronia.
  if (parcelId === null) return null;

  const route = parcel === undefined ? 'register' : resolveBilletIssueRoute(parcel);
  const isWorking =
    billet.phase === 'downloading' || billet.phase === 'registering' || billet.phase === 'polling';

  const parcelLabel =
    parcel?.numberParcel == null
      ? EMPTY_VALUE
      : numberParcels == null
        ? t('parcels.table.parcelNumberShort', { number: parcel.numberParcel })
        : t('parcels.table.parcelNumber', { number: parcel.numberParcel, total: numberParcels });

  const handleClose = () => {
    billet.reset();
    onClose();
  };

  return (
    <ConfirmDialog
      open
      tone="brand"
      icon={ArrowDownTrayIcon}
      title={
        route === 'download' ? t('parcels.billet.titleDownload') : t('parcels.billet.titleRegister')
      }
      description={
        route === 'download'
          ? t('parcels.billet.questionDownload')
          : t('parcels.billet.questionRegister')
      }
      confirmLabel={t('parcels.billet.confirm')}
      cancelLabel={t('parcels.billet.cancel')}
      closeAriaLabel={t('parcels.billet.close')}
      isPending={isWorking}
      confirmDisabled={billet.phase === 'ready'}
      onConfirm={() => billet.start(route)}
      onClose={handleClose}
    >
      <div className="flex flex-col gap-xxxs">
        <p className="ts-paragraph-xs text-ctx-content-placeholder">
          {t('parcels.billet.context', {
            parcel: parcelLabel,
            date: formatContractDate(parcel?.expirationDate),
            value: formatCurrency(parcel?.total),
          })}
        </p>

        {billet.phase === 'polling' && (
          <div className="flex flex-col gap-quark rounded-sm bg-ctx-layout-body p-mini">
            <span className="flex items-center gap-quark ts-paragraph-xs text-ctx-content-base">
              <Spinner size="sm" label={t('parcels.billet.waiting')} />
              {t('parcels.billet.waiting')}
            </span>
            <ProgressBar progressPercentage={(billet.attempts / billet.maxAttempts) * 100} />
          </div>
        )}

        {billet.phase === 'ready' && (
          <div className="flex flex-col gap-micro rounded-sm bg-ctx-layout-body p-mini">
            <Alert type="information" title={t('parcels.billet.ready')} />
            {/* O PDF já foi aberto em nova aba, mas o bloqueador de pop-up pode
                ter engolido: o botão é o caminho que sobra, e um clique explícito
                nunca é bloqueado. */}
            {billet.pdfUrl !== null && (
              <Button
                hierarchy="secondary"
                size="md"
                leadingIcon={ArrowDownTrayIcon}
                onClick={() => window.open(billet.pdfUrl ?? '', '_blank', 'noopener')}
              >
                {t('parcels.billet.openPdf')}
              </Button>
            )}
          </div>
        )}

        {billet.phase === 'timedOut' && (
          <Alert type="warning" title={t('parcels.billet.timedOut')} />
        )}

        {billet.phase === 'failed' && (
          <Alert
            type="danger"
            title={
              billet.failure === null
                ? t('parcels.billet.failedFallback')
                : t('parcels.billet.failed', { message: billet.failure.message })
            }
          />
        )}
      </div>
    </ConfirmDialog>
  );
}
